import React, { useState } from 'react';
import { Save, RefreshCw, Sliders, Bell, AlertTriangle } from 'lucide-react';
import { AnalysisConfiguration, MovementMetricType, SubtleChangeNotificationSettings } from './interfaces/types';
import { defaultAnalysisConfig } from './utils/PatternAnalysisEngine';

interface SettingsPanelProps {
  config: AnalysisConfiguration;
  notificationSettings: SubtleChangeNotificationSettings;
  onSaveConfig: (config: AnalysisConfiguration) => void;
  onSaveNotificationSettings: (settings: SubtleChangeNotificationSettings) => void;
  onRunAnalysis: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  config,
  notificationSettings,
  onSaveConfig,
  onSaveNotificationSettings,
  onRunAnalysis
}) => {
  const [analysisConfig, setAnalysisConfig] = useState<AnalysisConfiguration>({ ...config });
  const [notifications, setNotifications] = useState<SubtleChangeNotificationSettings>({ ...notificationSettings });
  const [activeTab, setActiveTab] = useState<'analysis' | 'notifications'>('analysis');
  
  // Handler for sensitivity level change
  const handleSensitivityChange = (level: 'LOW' | 'MEDIUM' | 'HIGH') => {
    setAnalysisConfig(prev => ({
      ...prev,
      sensitivityLevel: level
    }));
  };
  
  // Handler for toggling enabled metrics
  const toggleMetric = (metricType: MovementMetricType) => {
    setAnalysisConfig(prev => {
      const enabledMetrics = [...prev.enabledMetrics];
      if (enabledMetrics.includes(metricType)) {
        return {
          ...prev,
          enabledMetrics: enabledMetrics.filter(m => m !== metricType)
        };
      } else {
        return {
          ...prev,
          enabledMetrics: [...enabledMetrics, metricType]
        };
      }
    });
  };
  
  // Handler for saving analysis configuration
  const handleSaveAnalysisConfig = () => {
    onSaveConfig(analysisConfig);
  };
  
  // Handler for saving notification settings
  const handleSaveNotificationSettings = () => {
    onSaveNotificationSettings(notifications);
  };
  
  // Handler for resetting to defaults
  const handleResetToDefaults = () => {
    setAnalysisConfig({ ...defaultAnalysisConfig });
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('analysis')}
          className={`flex items-center py-3 px-4 ${
            activeTab === 'analysis' 
              ? 'text-blue-600 border-b-2 border-blue-500 font-medium' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Sliders size={18} className="mr-2" />
          Analysis Settings
        </button>
        
        <button
          onClick={() => setActiveTab('notifications')}
          className={`flex items-center py-3 px-4 ${
            activeTab === 'notifications' 
              ? 'text-blue-600 border-b-2 border-blue-500 font-medium' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Bell size={18} className="mr-2" />
          Notification Settings
        </button>
      </div>
      
      {/* Analysis Settings Content */}
      {activeTab === 'analysis' && (
        <div className="p-4">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Detection Sensitivity</h3>
            <p className="text-sm text-gray-600 mb-3">
              Adjust how sensitive the system is to subtle changes. Higher sensitivity detects smaller changes but may include more false positives.
            </p>
            
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => handleSensitivityChange('LOW')}
                className={`flex-1 py-2 ${
                  analysisConfig.sensitivityLevel === 'LOW' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Low
              </button>
              <button
                onClick={() => handleSensitivityChange('MEDIUM')}
                className={`flex-1 py-2 ${
                  analysisConfig.sensitivityLevel === 'MEDIUM' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Medium
              </button>
              <button
                onClick={() => handleSensitivityChange('HIGH')}
                className={`flex-1 py-2 ${
                  analysisConfig.sensitivityLevel === 'HIGH' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                High
              </button>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Analysis Time Window</h3>
            <p className="text-sm text-gray-600 mb-3">
              Set the time period for analyzing movement patterns.
            </p>
            
            <div className="flex items-center">
              <input
                type="range"
                min="7"
                max="180"
                step="7"
                value={analysisConfig.analysisWindowDays}
                onChange={(e) => setAnalysisConfig(prev => ({
                  ...prev,
                  analysisWindowDays: parseInt(e.target.value)
                }))}
                className="flex-1 mr-4"
              />
              <span className="bg-gray-100 px-3 py-1 rounded-lg text-sm">
                {analysisConfig.analysisWindowDays} days
              </span>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Metrics to Monitor</h3>
            <p className="text-sm text-gray-600 mb-3">
              Select which movement metrics to include in the analysis.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {(Object.keys(analysisConfig.detectionThresholds) as MovementMetricType[]).map(metricType => (
                <label key={metricType} className="flex items-center space-x-2 p-2 border rounded hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={analysisConfig.enabledMetrics.includes(metricType)}
                    onChange={() => toggleMetric(metricType)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-sm">{metricType.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Minimum Data Required</h3>
            <p className="text-sm text-gray-600 mb-3">
              Minimum number of data points required before analysis.
            </p>
            
            <div className="flex items-center">
              <input
                type="range"
                min="7"
                max="30"
                value={analysisConfig.minDataPointsRequired}
                onChange={(e) => setAnalysisConfig(prev => ({
                  ...prev,
                  minDataPointsRequired: parseInt(e.target.value)
                }))}
                className="flex-1 mr-4"
              />
              <span className="bg-gray-100 px-3 py-1 rounded-lg text-sm">
                {analysisConfig.minDataPointsRequired} points
              </span>
            </div>
          </div>
          
          <div className="flex justify-between mt-6">
            <button
              onClick={handleResetToDefaults}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw size={16} className="mr-2" />
              Reset to Defaults
            </button>
            
            <div className="space-x-2">
              <button
                onClick={onRunAnalysis}
                className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                Run Analysis Now
              </button>
              
              <button
                onClick={handleSaveAnalysisConfig}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                <Save size={16} className="mr-2" />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Notification Settings Content */}
      {activeTab === 'notifications' && (
        <div className="p-4">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Enable Notifications</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={notifications.notificationsEnabled} 
                  onChange={() => setNotifications(prev => ({
                    ...prev,
                    notificationsEnabled: !prev.notificationsEnabled
                  }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Turn notifications on or off for detected subtle changes.
            </p>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Minimum Significance Level</h3>
            <p className="text-sm text-gray-600 mb-3">
              Only notify for changes at or above this significance level.
            </p>
            
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setNotifications(prev => ({ ...prev, minSignificanceToNotify: 'MINOR' }))}
                className={`flex-1 py-2 ${
                  notifications.minSignificanceToNotify === 'MINOR' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Minor
              </button>
              <button
                onClick={() => setNotifications(prev => ({ ...prev, minSignificanceToNotify: 'MODERATE' }))}
                className={`flex-1 py-2 ${
                  notifications.minSignificanceToNotify === 'MODERATE' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Moderate
              </button>
              <button
                onClick={() => setNotifications(prev => ({ ...prev, minSignificanceToNotify: 'SIGNIFICANT' }))}
                className={`flex-1 py-2 ${
                  notifications.minSignificanceToNotify === 'SIGNIFICANT' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Significant
              </button>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Notification Frequency</h3>
            <p className="text-sm text-gray-600 mb-3">
              How often to send notifications about detected changes.
            </p>
            
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setNotifications(prev => ({ ...prev, notificationFrequency: 'IMMEDIATE' }))}
                className={`flex-1 py-2 ${
                  notifications.notificationFrequency === 'IMMEDIATE' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Immediate
              </button>
              <button
                onClick={() => setNotifications(prev => ({ ...prev, notificationFrequency: 'DAILY' }))}
                className={`flex-1 py-2 ${
                  notifications.notificationFrequency === 'DAILY' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Daily
              </button>
              <button
                onClick={() => setNotifications(prev => ({ ...prev, notificationFrequency: 'WEEKLY' }))}
                className={`flex-1 py-2 ${
                  notifications.notificationFrequency === 'WEEKLY' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Weekly
              </button>
            </div>
          </div>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="includeRecommendations"
                checked={notifications.includeRecommendations}
                onChange={() => setNotifications(prev => ({
                  ...prev,
                  includeRecommendations: !prev.includeRecommendations
                }))}
                className="h-4 w-4 text-blue-600"
              />
              <label htmlFor="includeRecommendations" className="ml-2 text-sm text-gray-700">
                Include recommended actions in notifications
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="autoGenerateReports"
                checked={notifications.autoGenerateReports}
                onChange={() => setNotifications(prev => ({
                  ...prev,
                  autoGenerateReports: !prev.autoGenerateReports
                }))}
                className="h-4 w-4 text-blue-600"
              />
              <label htmlFor="autoGenerateReports" className="ml-2 text-sm text-gray-700">
                Automatically generate reports for significant changes
              </label>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Caregivers to Notify</h3>
            <p className="text-sm text-gray-600 mb-3">
              Select which caregivers should receive change notifications.
            </p>
            
            <div className="space-y-2">
              {['Dr. Sarah Johnson', 'Physical Therapist Michael', 'Family Caregiver - Martha'].map((caregiver, index) => (
                <div key={index} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`caregiver-${index}`}
                    checked={notifications.caregiversToNotify.includes(caregiver)}
                    onChange={() => {
                      setNotifications(prev => {
                        const caregivers = [...prev.caregiversToNotify];
                        if (caregivers.includes(caregiver)) {
                          return {
                            ...prev,
                            caregiversToNotify: caregivers.filter(c => c !== caregiver)
                          };
                        } else {
                          return {
                            ...prev,
                            caregiversToNotify: [...caregivers, caregiver]
                          };
                        }
                      });
                    }}
                    className="h-4 w-4 text-blue-600"
                  />
                  <label htmlFor={`caregiver-${index}`} className="ml-2 text-sm text-gray-700">
                    {caregiver}
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <button
              onClick={handleSaveNotificationSettings}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <Save size={16} className="mr-2" />
              Save Notification Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;